import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Bookmark, 
  ArrowRight, 
  Home, 
  LogOut, 
  Trash2, 
  ArrowLeft,
  Heart,
  Clock,
  FileText,
  Menu,
  X,
  Briefcase,
  Building2,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SavedSchemes = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('schemes'); // 'schemes' | 'jobs'
  const [savedSchemes, setSavedSchemes] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchSavedSchemes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/saved-schemes`);
        setSavedSchemes(response.data);
      } catch (error) {
        console.error('Error fetching saved schemes:', error);
      } finally {
        setLoading(false);
      }
    };

    // Saved jobs use a different auth shape — the /api/jobs/saved route reads
    // the Bearer token directly via `get_current_user` rather than the cookie
    // session that powers /api/saved-schemes.
    const fetchSavedJobs = async () => {
      try {
        setJobsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setSavedJobs([]);
          return;
        }
        const response = await axios.get(`${BACKEND_URL}/api/jobs/saved`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedJobs(response.data?.saved_jobs || []);
      } catch (error) {
        console.error('Error fetching saved jobs:', error);
        setSavedJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };
    
    fetchSavedSchemes();
    fetchSavedJobs();
  }, []);

  const handleUnsave = async (schemeId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${BACKEND_URL}/api/saved-schemes/unsave/${schemeId}`);
      setSavedSchemes(prev => prev.filter(s => s.id !== schemeId));
    } catch (error) {
      console.error('Error removing saved scheme:', error);
    }
  };

  const handleUnsaveJob = async (jobId, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/jobs/save/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedJobs((prev) => prev.filter((j) => j.job_id !== jobId));
    } catch (error) {
      console.error('Error removing saved job:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      {/* Header */}
      <header className="border-b bg-white/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo.jpeg" alt="Logo" className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl shadow-lg object-cover" />
              <div className="hidden sm:block">
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                  {t('appName')}
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-600">{t('saved')}</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              <LanguageSwitcher />
              <Button 
                onClick={() => navigate('/schemes')} 
                variant="outline" 
                size="sm"
                className="border-green-600 text-green-700 hover:bg-green-50"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('allSchemes')}
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline" 
                size="sm"
                className="border-green-600 text-green-700 hover:bg-green-50"
              >
                <Home className="w-4 h-4 mr-1" />
                {t('home')}
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <LogOut className="w-4 h-4 mr-1" />
                {t('logout')}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSwitcher variant="compact" />
              <button 
                className="p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pt-3 border-t border-gray-200 space-y-2">
              <Button 
                onClick={() => { navigate('/schemes'); setMobileMenuOpen(false); }} 
                variant="outline"
                className="w-full border-green-600 text-green-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('allSchemes')}
              </Button>
              <Button 
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }} 
                variant="outline"
                className="w-full border-green-600 text-green-700"
              >
                <Home className="w-4 h-4 mr-2" />
                {t('home')}
              </Button>
              <Button 
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full border-gray-300 text-gray-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Page Title */}
        <div className="mb-4 sm:mb-6 text-center">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full">
              <Heart className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
            <span className="bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              {t('yourSavedSchemes') || 'Your Saved Items'}
            </span>
          </h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg">
            {activeTab === 'schemes'
              ? (loading ? '...' : `${savedSchemes.length} saved ${savedSchemes.length === 1 ? 'scheme' : 'schemes'}`)
              : (jobsLoading ? '...' : `${savedJobs.length} saved ${savedJobs.length === 1 ? 'job' : 'jobs'}`)}
          </p>
        </div>

        {/* Tab switcher — Schemes / Jobs */}
        <div className="mb-6 sm:mb-8 flex justify-center" data-testid="saved-tabs">
          <div className="inline-flex rounded-full bg-white shadow-sm border border-slate-200 p-1">
            <button
              type="button"
              onClick={() => setActiveTab('schemes')}
              data-testid="saved-tab-schemes"
              className={`inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeTab === 'schemes'
                  ? 'bg-green-600 text-white shadow'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              Schemes
              <span className={`ml-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] ${
                activeTab === 'schemes' ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
              }`}>{savedSchemes.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('jobs')}
              data-testid="saved-tab-jobs"
              className={`inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeTab === 'jobs'
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Jobs
              <span className={`ml-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] ${
                activeTab === 'jobs' ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
              }`}>{savedJobs.length}</span>
            </button>
          </div>
        </div>

        {activeTab === 'schemes' ? (
          // ───────────── Saved Schemes tab ─────────────
          <>
        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12 sm:py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-16 sm:w-16 border-b-2 border-pink-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-sm sm:text-lg">{t('loadingSchemes')}</p>
            </div>
          </div>
        ) : savedSchemes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {savedSchemes.map((scheme) => (
              <Card 
                key={scheme.id} 
                className="p-4 sm:p-5 md:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border-none bg-white shadow-md group relative"
                onClick={() => navigate(`/schemes/${scheme.slug || scheme.id}`)}
              >
                {/* Remove Button */}
                <button
                  onClick={(e) => handleUnsave(scheme.id, e)}
                  className="absolute top-3 sm:top-4 right-3 sm:right-4 p-1.5 sm:p-2 rounded-full bg-pink-100 text-pink-600 hover:bg-pink-200 transition-colors"
                  title={t('removeFromSaved')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 pr-10 sm:pr-12">
                  <Badge 
                    className={`text-[10px] sm:text-xs ${
                      scheme.state === 'Andhra Pradesh' 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                        : scheme.state === 'Telangana'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                        : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                    }`}
                  >
                    {scheme.state}
                  </Badge>
                  <Badge 
                    className={`text-[10px] sm:text-xs ${
                      scheme.type === 'state' 
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                        : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    }`}
                  >
                    {scheme.type === 'state' ? t('stateType') : t('central')}
                  </Badge>
                </div>

                <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 leading-tight text-slate-800 group-hover:text-green-700 transition-colors line-clamp-2">
                  {scheme.name}
                </h3>
                <p className="text-gray-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3 text-xs sm:text-sm md:text-base">
                  {scheme.shortDescription}
                </p>

                <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                    <span className="font-semibold text-gray-700">{t('sector')}:</span>
                    <Badge variant="outline" className="text-[10px] sm:text-xs border-green-300 text-green-700">{scheme.sector}</Badge>
                  </div>
                  {scheme.saved_at && (
                    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-500">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>{t('savedOn')} {formatDate(scheme.saved_at)}</span>
                    </div>
                  )}
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-md text-xs sm:text-sm py-2 sm:py-2.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/schemes/${scheme.slug || scheme.id}`);
                  }}
                >
                  {t('viewDetails')}
                  <ArrowRight className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Button>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 sm:p-12 text-center border-none bg-white shadow-xl">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bookmark className="w-8 h-8 sm:w-10 sm:h-10 text-pink-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 text-slate-800">{t('noSavedSchemesYet')}</h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {t('startExploring')}
              </p>
              <Button 
                onClick={() => navigate('/schemes')} 
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
              >
                <FileText className="mr-2 w-4 h-4" />
                {t('browseAllSchemes')}
              </Button>
            </div>
          </Card>
        )}
          </>
        ) : (
          // ───────────── Saved Jobs tab ─────────────
          <>
            {jobsLoading ? (
              <div className="flex justify-center items-center py-12 sm:py-20" data-testid="saved-jobs-loading">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-16 sm:w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm sm:text-lg">Loading saved jobs…</p>
                </div>
              </div>
            ) : savedJobs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6" data-testid="saved-jobs-grid">
                {savedJobs.map((job) => (
                  <Card
                    key={job.job_id}
                    data-testid={`saved-job-card-${job.job_id}`}
                    onClick={() => navigate(`/schemes/jobs/${job.slug || job.job_id}`)}
                    className="p-4 sm:p-5 md:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border-none bg-white shadow-md group relative"
                  >
                    <button
                      onClick={(e) => handleUnsaveJob(job.job_id, e)}
                      className="absolute top-3 sm:top-4 right-3 sm:right-4 p-1.5 sm:p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                      title="Remove from saved"
                      data-testid={`saved-job-remove-${job.job_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 pr-10 sm:pr-12">
                      {job.category && (
                        <Badge className="text-[10px] sm:text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                          {job.category}
                        </Badge>
                      )}
                      {job.location && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs border-slate-300 text-slate-600">
                          <MapPin className="w-3 h-3 mr-0.5 inline" />
                          {job.location}
                        </Badge>
                      )}
                    </div>

                    <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 leading-tight text-slate-800 group-hover:text-blue-700 transition-colors line-clamp-2">
                      {job.title || 'Untitled job'}
                    </h3>

                    {job.department && (
                      <p className="text-xs sm:text-sm text-slate-600 mb-2 flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span className="line-clamp-1">{job.department}</span>
                      </p>
                    )}

                    <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 text-xs sm:text-sm text-slate-600">
                      {job.last_date && (
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
                          <span>Apply by <span className="font-semibold text-slate-800">{job.last_date}</span></span>
                        </div>
                      )}
                      {job.saved_at && (
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <Heart className="w-3 h-3" />
                          <span>{t('savedOn')} {formatDate(job.saved_at)}</span>
                        </div>
                      )}
                    </div>

                    <Button
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md text-xs sm:text-sm py-2 sm:py-2.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/schemes/jobs/${job.slug || job.job_id}`);
                      }}
                    >
                      View details
                      <ArrowRight className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </Button>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 sm:p-12 text-center border-none bg-white shadow-xl" data-testid="saved-jobs-empty">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-2 text-slate-800">No saved jobs yet</h3>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">
                    Tap the heart on any recruitment notification to save it here.
                  </p>
                  <Button
                    onClick={() => navigate('/schemes?tab=jobs')}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                  >
                    <Briefcase className="mr-2 w-4 h-4" />
                    Browse all jobs
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SavedSchemes;
